import React, { FC, useState } from "react";
import { observer } from "mobx-react";

import Popup from "../../Popup";
import { chatService } from "../../../../services/chatService";
import { useChatStore } from "../../../../hooks/useChatStore";
import useNotifier from "../../../../hooks/useNotifier";

interface CloseChatConfirmationDialogProps {
  onChatClosed: (chatName: string, closedAt: string) => void;
}

const CloseChatConfirmationDialog: FC<CloseChatConfirmationDialogProps> = observer(
  ({ onChatClosed }) => {
    const chatStore = useChatStore();
    const notifier = useNotifier();
    const [isClosing, setIsClosing] = useState(false);

    const chat = chatStore.showClosePopup;

    const handleClose = () => {
      chatStore.setShowClosePopup(null);
    };

    const handleConfirmClose = async () => {
      if (!chat) return;
      setIsClosing(true);

      try {
        const response = await chatService.closeChat(chat.name);
        if (response.success) {
          const closedAt = response.data?.closed_at;
          if (!closedAt) {
            notifier.showError("Chat was closed, but the status could not be updated. Please refresh.");
            return;
          }
          onChatClosed(chat.name, closedAt);
          if (chat.name === chatStore.activeChatId) {
            chatStore.markChatClosed(closedAt);
          }
        } else {
          notifier.showError(response?.error || "Failed to close chat. Please try again.");
        }
      } catch (error) {
        notifier.showError(
          error instanceof Error ? error.message : "An error occurred while closing the chat. Please try again.",
        );
      } finally {
        setIsClosing(false);
        chatStore.setShowClosePopup(null);
      }
    };

    return (
      <Popup
        isOpen={!!chat}
        onClose={handleClose}
        title="Close Chat"
        message={`Close "${chat?.label || chat?.name}"? Closed chats become read-only and cannot be reopened.`}
        actions={[
          {
            label: "Cancel",
            onClick: handleClose,
            variant: "secondary",
            disabled: isClosing,
          },
          {
            label: "Close chat",
            onClick: handleConfirmClose,
            variant: "danger",
            disabled: isClosing,
            loading: isClosing,
          },
        ]}
      />
    );
  },
);

export default CloseChatConfirmationDialog;
